import { DomainError } from "@szerviz/core";
import { Logo } from "../components/Logo";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { LanguageSelect } from "../components/LanguageSelect";
import type { DataStore } from "../data";

export function Login({ store, notice }: { store: DataStore; notice?: string | null }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<"signIn" | "signUp" | "reset">("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === "reset") {
        await store.requestPasswordReset(email.trim());
        setInfo(t("auth.resetSent"));
        setMode("signIn");
      } else if (mode === "signIn") await store.signIn(email.trim(), password);
      else {
        const { needsConfirmation } = await store.signUp(email.trim(), password, i18n.language);
        if (needsConfirmation) {
          setInfo(t("auth.confirmEmail"));
          setMode("signIn");
        }
      }
    } catch (err) {
      // an unconfirmed e-mail address says so ("Erősítse meg az e-mail címét"); other sign in errors stay general
      setError(mode === "signIn" && !(err instanceof DomainError && err.code === "EMAIL_NOT_CONFIRMED") ? new Error(t("auth.failed")) : err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-card">
        <div className="brand">
          <Logo size={40} />
        </div>
        <p className="tagline">{t("app.tagline")}</p>
        <h1>{t(mode === "signIn" ? "auth.title.signIn" : mode === "signUp" ? "auth.title.signUp" : "auth.title.reset")}</h1>
        {mode === "reset" && <p>{t("auth.resetIntro")}</p>}
        {notice && mode === "signIn" && <p className="info" role="status">{t(notice)}</p>}
        <form onSubmit={submit}>
          <label className="field wide">
            <span>{t("auth.email")}</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-label={t("auth.email")} autoComplete="email" />
          </label>
          {mode !== "reset" && <label className="field wide">
            <span>{t("auth.password")}</span>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-label={t("auth.password")}
              autoComplete={mode === "signIn" ? "current-password" : "new-password"}
            />
            {mode === "signUp" && <small>{t("auth.passwordHint")}</small>}
          </label>}
          {info && <p className="info">{info}</p>}
          <ErrorText error={error} />
          <button type="submit" className="primary" disabled={busy}>
            {t(mode === "signIn" ? "auth.signIn" : mode === "signUp" ? "auth.signUp" : "auth.sendReset")}
          </button>
        </form>
        {mode === "signIn" && (
          <button type="button" className="link" onClick={() => setMode("reset")}>
            {t("auth.forgotPassword")}
          </button>
        )}
        {mode !== "signIn" && (
          <button type="button" className="link" onClick={() => setMode("signIn")}>
            {t("auth.toSignIn")}
          </button>
        )}
        {mode === "signIn" && store.allowSignUp && (
          <button type="button" className="link" onClick={() => setMode("signUp")}>
            {t("auth.toSignUp")}
          </button>
        )}
        {mode === "signIn" && !store.allowSignUp && <p className="muted small">{t("auth.inviteOnly")}</p>}
        {store.mode === "demo" && <p className="note">{t("demo.banner")}</p>}
        <div className="login-lang">
          <LanguageSelect />
        </div>
      </div>
    </div>
  );
}
